// ============================================================
// PhotoBrowser.jsx — grid picker over the client's brand_photos
// ------------------------------------------------------------
// Single-select (click → edit) plus a "Select Multiple" toggle that
// collects photos for batch editing (batch save lands in Phase 2).
// Mirrors the brand_photos load query used elsewhere in the dashboard.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { clientScopedFetch } from '../../lib/authedFetch';
import { useViewingAs } from '../../hooks/useViewingAs';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

export default function PhotoBrowser({ clientId, onPick, onPickMultiple, refreshKey = 0 }) {
  // Phase 6: clientId is the SIGNED-IN user (the panel's prop), so this grid
  // showed the admin's own library while viewing a client. The scope now comes
  // from the route; viewingAsClientId is what makes it re-read on a context
  // change, since clientId itself never changes.
  const { viewingAsClientId } = useViewingAs();
  const [photos, setPhotos]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [multi, setMulti]     = useState(false);
  const [selected, setSelected] = useState([]); // ids

  useEffect(() => {
    let alive = true;
    if (!clientId) { setLoading(false); return; }
    setLoading(true);
    // Shares /api/photo-library with PhotoLibraryPanel — same ordering, one
    // definition, so the two grids cannot drift apart. The settings half of
    // that payload is ignored here.
    clientScopedFetch(`${BACKEND}/api/photo-library`)
      .then(async (res) => {
        if (!alive) return;
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.ok) { setError(body.error || `HTTP ${res.status}`); }
        else { setPhotos(body.photos || []); }
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err.message);
        setLoading(false);
      });
    return () => { alive = false; };
  }, [clientId, viewingAsClientId, refreshKey]);

  const toggleSelect = useCallback((photo) => {
    setSelected((prev) =>
      prev.includes(photo.id) ? prev.filter((id) => id !== photo.id) : [...prev, photo.id],
    );
  }, []);

  const handleClick = (photo) => {
    if (multi) { toggleSelect(photo); return; }
    onPick(photo);
  };

  const startBatch = () => {
    const chosen = photos.filter((p) => selected.includes(p.id));
    if (chosen.length === 0) return;
    if (chosen.length === 1) { onPick(chosen[0]); return; }
    onPickMultiple?.(chosen);
  };

  if (loading) {
    return <div className="pe-empty">Loading your photos…</div>;
  }
  if (error) {
    return <div className="pe-empty">Couldn’t load photos: {error}</div>;
  }
  if (photos.length === 0) {
    return (
      <div className="pe-empty">
        No photos in your library yet — upload some in the <strong>Photo Library</strong> tab.
      </div>
    );
  }

  return (
    <div className="pe-browser">
      <div className="pe-browser-toolbar">
        <div className="pe-browser-title">Choose a photo to edit</div>
        <div className="pe-browser-actions">
          {multi && selected.length > 0 && (
            <button className="pe-btn pe-btn-primary" onClick={startBatch}>
              Edit {selected.length} selected
            </button>
          )}
          <button
            className={`pe-btn pe-btn-ghost${multi ? ' active' : ''}`}
            onClick={() => { setMulti((m) => !m); setSelected([]); }}
          >
            {multi ? 'Cancel' : 'Select Multiple'}
          </button>
        </div>
      </div>

      <div className="pe-grid">
        {photos.map((photo) => {
          const isSel = selected.includes(photo.id);
          return (
            <div
              key={photo.id}
              className={`pe-tile${isSel ? ' selected' : ''}`}
              onClick={() => handleClick(photo)}
            >
              <img src={photo.url} alt={photo.filename || 'brand photo'} loading="lazy" />
              {photo.used && <span className="pe-tile-badge">used</span>}
              {multi ? (
                <span className={`pe-tile-check${isSel ? ' on' : ''}`}>{isSel ? '✓' : ''}</span>
              ) : (
                <div className="pe-tile-hover">
                  <span className="pe-btn pe-btn-primary pe-btn-sm">Edit</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
